import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { toast } from '@hivebrite/design-system/Toast';

import BuilderThemeProvider from 'hb-builder/components/common/BuilderThemeProvider';
import { preloadImage } from 'hb-builder/components/common/ImageSetter/utils/preloadImage';
import { useBuilderConfigContext } from 'hb-builder/contexts/BuilderConfigContext';
import { useBuilderContext, useModalContext } from 'hb-builder/hooks';
import ImageLibraryModal from 'hb-react/backoffice/components/ImageLibrary/components/Modal';
import { convertToFile } from 'hb-react/backoffice/components/ImageLibrary/utils/files';
import { overrideFileName } from 'hb-react/backoffice/utils/overrideFileName';
import { VirusScanStatus } from 'hb-react/shared/hooks/useGlobalStorageV2';
import { getFileValidator } from 'hb-react/shared/hooks/useGlobalStorageV2/utils/validators';
import useVirusScanningEnabled from 'hb-react/shared/hooks/useVirusScanningEnabled';
import { getImagePreview } from 'hb-react/shared/utils/image';

const ACCEPT_IMAGES = ['image/gif', 'image/png', 'image/jpeg'];

export const IMAGE_LIBRARY_ACTIVATED_FEATURES = Object.freeze({
  cropAndZoom: true,
  unsplash: true,
  upload: true,
  url: true,
});

let removeImageDataTries = 0;

const timeout = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ImageSetter = ({ attributeName, item, imageSize = '2000x2000' }) => {
  const { updateAttribute, updateAttributes } = useBuilderContext();
  const virusScanningEnabled = useVirusScanningEnabled(); // Only used for global storage
  const [fileKey, setFileKey] = useState(null); // Only used for global storage
  const [storageKey, setStorageKey] = useState(null); // Only used for global storage
  const [isScanningVirus, setIsScanningVirus] = useState(null); // Only used for global storage
  const [imagePreviewData, setImagePreviewData] = useState(null); // Only used for global storage
  const {
    features: {
      image: { onUpload, onSave, files, shouldUseGlobalStorage },
    },
    hivebrite: {
      ids: { network: networkId },
    },
  } = useBuilderConfigContext();

  const { isModalOpen, setModalOpen } = useModalContext();

  const handleCloseModal = useCallback(() => {
    setIsScanningVirus(null);
    setModalOpen((prevIsModalOpen) => ({
      ...prevIsModalOpen,
      [item?.id]: false,
    }));
  }, [item, setModalOpen]);

  const fileValidator = useMemo(
    () => getFileValidator(virusScanningEnabled),
    [virusScanningEnabled],
  );

  const removeImageData = async (imageUrl) => {
    // To prevent a flash with the image when transitioning from the data-image to the src attribute),
    // we preload the image with its URL and then remove the data-image attribute.
    // When the image was just saved, there's a delay before it's available and we can get it so we are retrying until the image is successfully loaded.
    if (removeImageDataTries > 5) {
      removeImageDataTries = 0;

      return;
    }

    removeImageDataTries++;

    await preloadImage(imageUrl)
      .then(() => {
        updateAttribute({
          id: item.id,
          attribute: '_imageData',
          value: undefined,
        });
        removeImageDataTries = 0;
      })
      .catch(async () => {
        await timeout(1000 * removeImageDataTries);
        await removeImageData(imageUrl);
      });
  };

  const updateAttributesWhenImgCreated = async (
    imageUrl,
    shouldRemoveImageData,
  ) => {
    updateAttributes({
      id: item.id,
      values: [
        {
          attribute: attributeName,
          value: imageUrl,
        },
        {
          attribute: 'isLoadingSrc',
          value: undefined,
        },
      ],
    });

    if (shouldRemoveImageData) {
      // Needed for perf when there are too many images in the document
      await removeImageData(imageUrl);
    }
  };

  const handleSave = async ({ image, isUpload }) => {
    if (image && initialImage(item) !== image) {
      // If the image is already an url, no need for it to be uploaded to be saved.
      if (typeof image === 'string' && image.startsWith('http')) {
        updateAttributes({
          id: item.id,
          values: [
            {
              attribute: '_imageData',
              value: undefined,
            },
            {
              attribute: attributeName,
              value: image,
            },
          ],
        });
      } else {
        const fileImage = await getFileImage(image);

        updateAttribute({
          id: item.id,
          attribute: 'isLoadingSrc',
          value: true,
        });

        handleCloseModal();

        try {
          if (shouldUseGlobalStorage) {
            await handleSaveViaGlobalStorage(fileImage, isUpload);
          } else {
            const imageUrl = await onSave({ file: fileImage });

            await updateAttributesWhenImgCreated(imageUrl);
          }
        } catch (err) {
          handleError(err);
        }
      }
    }
  };

  const getFileImage = (image) =>
    image instanceof File ? image : convertToFile({ imageString: image });

  const handleError = (error) => {
    updateAttributes({
      id: item.id,
      values: [
        {
          attribute: '_imageData',
          value: undefined,
        },
        {
          attribute: 'isLoadingSrc',
          value: undefined,
        },
      ],
    });
    if (error?.response?.status === 413) {
      toast.error(s__('File too large'), {
        // @ts-expect-error untyped
        text: s__(
          'There has been an issue while saving the image. Please try again with a lighter image.',
        ),
      });
    } else {
      toast.error(s__('ImageLibrary|An error occurred while saving the file'));
    }
  };

  const handleSaveViaGlobalStorage = async (fileImage, isUpload) => {
    // Set a temporary attribute to display the img tag with the data-image and not the src attribute and make the image appear instantly
    updateAttribute({
      id: item.id,
      attribute: '_imageData',
      value: imagePreviewData,
    });

    // Reset the image data state
    setImagePreviewData(null);

    try {
      const imageUrl = await onSave({
        file: fileImage,
        key: isUpload ? fileKey : undefined,
        shrineKey: storageKey,
      });

      updateAttributesWhenImgCreated(imageUrl, true);
    } catch (error) {
      handleError(error);
    }
  };

  // Only used for global storage
  const handleUploadFile = async ({ file }) => {
    if (ACCEPT_IMAGES.includes(file.type)) {
      const { key } = await onUpload(overrideFileName({ file }));

      // Store the file to be able to retrieve the file later in the "files" array that we're getting back from useGlobalStorageFiles
      setFileKey(key);

      if (shouldUseGlobalStorage) {
        // Disable the save button while the virus scanning is in progress
        // This is switched back to false when the virus scanning is done, after looking at the content of "files" in a useEffect
        setIsScanningVirus(true);
      }

      const imagePreview = await getImagePreview(file);

      if (shouldUseGlobalStorage) {
        setImagePreviewData(imagePreview);
      }

      return imagePreview;
    }
  };

  useEffect(() => {
    // Wait for virus scanning to be done before allowing the user to save the image
    const file = files?.find((f) => f.key === fileKey);
    const key = file?.shrineKey;

    if (file?.virusScanStatus !== VirusScanStatus.PENDING && isScanningVirus) {
      const error = fileValidator(file);

      setIsScanningVirus(false);

      if (key) {
        setStorageKey(key);
      }

      if (error) {
        toast.error(error);
      }
    }
  }, [files, fileKey]);

  const initialImage = (item) => get(item?.attributes, attributeName);

  if (!isModalOpen[item.id]) {
    return null;
  }

  return (
    <BuilderThemeProvider>
      <Box onMouseDown={(e) => e.stopPropagation()}>
        <ImageLibraryModal
          features={IMAGE_LIBRARY_ACTIVATED_FEATURES}
          imageSize={imageSize}
          initial={initialImage(item)}
          isPendingChecks={isScanningVirus}
          networkId={networkId}
          onClose={handleCloseModal}
          onSave={handleSave}
          onUpload={handleUploadFile}
        />
      </Box>
    </BuilderThemeProvider>
  );
};

export default ImageSetter;
